import { useState } from "react";
import { Input, message } from "antd";
import {
  FiSearch, FiMail, FiPhone, FiMessageSquare,
  FiSend, FiRefreshCw, FiShield, FiGlobe,
  FiChevronDown, FiChevronUp,
} from "react-icons/fi";
import MainLayout from "../layout/MainLayout";

const FAQS = [
  {
    category: "Account",
    q: "How do I add funds to my wallet?",
    a: "Go to Dashboard or Mobile Wallet and click 'Add Funds'. Choose a source (bank transfer), enter the amount, and get a reference number. Once the system confirms the deposit, the funds appear in your wallet.",
  },
  {
    category: "Account",
    q: "Why is my account showing 'Identity verification required'?",
    a: "Before you can move funds, we must verify your identity (KYC) under the AML/CTF Act. Click 'Complete KYC' on the dashboard banner and follow the prompts. This typically takes under 5 minutes.",
  },
  {
    category: "Account",
    q: "How do I change my email or password?",
    a: "Email and password are managed by your ARM Financial identity provider. Visit the IdP profile page to update them. Changes reflect on ARM Financial after your next sign-in.",
  },
  {
    category: "Transfers",
    q: "What is the FX rate I get on conversions?",
    a: "We use the mid-market rate plus a small transparent fee shown before you confirm. There is no hidden FX spread, what you see is what you pay.",
  },
  {
    category: "Transfers",
    q: "How long does an international transfer take?",
    a: "Most transfers settle within a few hours. Depending on the destination country, banking partner, and compliance checks, it can take up to 48 hours.",
  },
  {
    category: "Transfers",
    q: "I sent money to the wrong recipient, can I cancel it?",
    a: "If the transfer is still in PENDING state, cancel it from the Pending Transactions page. Once processed and sent to the recipient bank, cancellation is not possible, contact support immediately.",
  },
  {
    category: "Transfers",
    q: "What currencies do you support?",
    a: "AUD, USD, and EUR. Additional corridors will appear in the wallet picker on the Dashboard as they are launched.",
  },
  {
    category: "Security",
    q: "How is my money protected?",
    a: "Funds are held in segregated accounts with licensed banking partners. We use bank-grade encryption (TLS 1.3) for all data in transit and AES-256 at rest. Two-factor authentication is strongly recommended.",
  },
  {
    category: "Security",
    q: "What should I do if I notice suspicious activity?",
    a: "Immediately click 'Sign out all devices' in Settings > Security, then contact support at support@armf.com. We will freeze your account and investigate within 2 hours.",
  },
  {
    category: "Billing",
    q: "Are there any fees for transfers?",
    a: "A small transparent fee is shown on the review screen before you confirm any transfer. There are no monthly account fees or hidden charges.",
  },
];

const CATEGORIES = ["All", ...Array.from(new Set(FAQS.map((f) => f.category)))];

const ContactCard = ({ icon, title, detail, action, onClick, color }) => (
  <div
    className="help-contact-card rounded-4 p-4 d-flex flex-column gap-3 cursor-pointer"
    style={{
      border: "1px solid var(--border-color)",
      background: "var(--card-bg)",
      transition: "transform 0.15s, box-shadow 0.15s",
    }}
    onClick={onClick}
    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
  >
    <div
      className="help-contact-icon d-flex align-items-center justify-content-center rounded-3"
      style={{ width: "44px", height: "44px", background: color + "20" }}
    >
      <span style={{ color }}>{icon}</span>
    </div>
    <div>
      <div className="fw-semibold" style={{ fontSize: "15px" }}>{title}</div>
      <div style={{ color: "var(--text-muted)", fontSize: "13px", marginTop: "2px" }}>{detail}</div>
    </div>
    <span style={{ color, fontWeight: 600, fontSize: "13px" }}>{action}</span>
  </div>
);

const HelpCenter = () => {
  const [query, setQuery] = useState("");
  const [openIdx, setOpenIdx] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = FAQS.filter((f) => {
    const catMatch = activeCategory === "All" || f.category === activeCategory;
    const q = query.trim().toLowerCase();
    const textMatch = !q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q);
    return catMatch && textMatch;
  });

  return (
    <MainLayout
      expand={false}
      NavName="Help Center"
      pageClassName="dashboard-page help-page"
      contentClassName="dashboard-content"
    >
      <div className="help-shell">

        {/* Hero */}
        <div className="help-hero text-center mb-5">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{ width: "64px", height: "64px", background: "rgba(27,187,107,0.12)" }}
          >
            <FiMessageSquare size={28} style={{ color: "#1bbb6b" }} />
          </div>
          <h1 className="fs-2 fw-bold m-0">How can we help?</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "14px", marginTop: "8px" }}>
            Search our knowledge base or reach out to our support team.
          </p>
          <div className="d-flex justify-content-center mt-3">
            <Input
              size="large"
              allowClear
              prefix={<FiSearch style={{ color: "var(--text-muted)" }} />}
              placeholder="Search articles, e.g. 'how to send money'"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpenIdx(null); }}
              style={{ maxWidth: "540px", borderRadius: "12px" }}
            />
          </div>
        </div>

        {/* Contact Cards */}
        <div className="row g-3 mb-5">
          <div className="col-md-4">
            <ContactCard
              icon={<FiMail size={22} />}
              title="Email Support"
              detail="Average reply under 4 hours on business days."
              action="support@armf.com"
              color="#1bbb6b"
              onClick={() => (window.location.href = "mailto:support@armf.com")}
            />
          </div>
          <div className="col-md-4">
            <ContactCard
              icon={<FiPhone size={22} />}
              title="Phone"
              detail="Talk to a human, 9am-6pm AEST Mon-Fri."
              action="+61 3 8679 2233"
              color="#4096ff"
              onClick={() => (window.location.href = "tel:+61386792233")}
            />
          </div>
          <div className="col-md-4">
            <ContactCard
              icon={<FiMessageSquare size={22} />}
              title="Report an Issue"
              detail="Found a bug or unexpected behaviour?"
              action="Open a ticket"
              color="#F7A64F"
              onClick={() =>
                message.info("Ticket form coming soon, please email support@armf.com for now.")
              }
            />
          </div>
        </div>

        {/* Quick Links */}
        <div
          className="help-card help-quick-links rounded-4 p-4 mb-5"
          style={{ background: "var(--card-bg)", border: "1px solid var(--border-color)" }}
        >
          <h2 className="fs-6 fw-semibold mb-3">Quick Links</h2>
          <div className="row g-2">
            {[
              { icon: <FiSend size={16} />, label: "Send Money", href: "/new-transfer" },
              { icon: <FiRefreshCw size={16} />, label: "Convert Currency", href: "/convert" },
              { icon: <FiShield size={16} />, label: "KYC / Identity Verification", href: "/settings" },
              { icon: <FiGlobe size={16} />, label: "Pending Transactions", href: "/pending-transactions" },
            ].map(({ icon, label, href }) => (
              <div className="col-6 col-md-3" key={label}>
                <a
                  href={href}
                  className="help-quick-link d-flex align-items-center gap-2 rounded-3 px-3 py-2 text-decoration-none"
                  style={{ background: "var(--bg-main)", border: "1px solid var(--border-color)", fontSize: "13px", color: "var(--text-main)", transition: "background 0.15s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(27,187,107,0.08)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--bg-main)")}
                >
                  <span style={{ color: "var(--accent-color)" }}>{icon}</span>
                  {label}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div
          className="help-card help-faq rounded-4 overflow-hidden"
          style={{ border: "1px solid var(--border-color)", background: "var(--card-bg)" }}
        >
          <div className="px-4 py-3" style={{ borderBottom: "1px solid var(--border-color)" }}>
            <h2 className="fs-5 fw-bold m-0">Frequently Asked Questions</h2>
          </div>

          {/* Category Tabs */}
          <div className="help-category-tabs d-flex gap-2 px-4 py-3 flex-wrap" style={{ borderBottom: "1px solid var(--border-color)" }}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setOpenIdx(null); }}
                className="btn btn-sm"
                style={{
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: 600,
                  background: activeCategory === cat ? "#1bbb6b" : "transparent",
                  color: activeCategory === cat ? "#fff" : "var(--text-muted)",
                  border: activeCategory === cat ? "1px solid #1bbb6b" : "1px solid var(--border-color)",
                  transition: "all 0.15s",
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div>
            {filtered.length === 0 ? (
              <div className="py-5 text-center" style={{ color: "var(--text-muted)" }}>
                <FiSearch size={28} style={{ opacity: 0.4 }} />
                <p className="mt-2 mb-0" style={{ fontSize: "14px" }}>
                  No results for "<strong>{query}</strong>", try a different keyword.
                </p>
              </div>
            ) : (
              filtered.map((faq, idx) => {
                const isOpen = openIdx === idx;
                return (
                  <div
                    key={idx}
                    style={{ borderBottom: idx < filtered.length - 1 ? "1px solid var(--border-color)" : "none" }}
                  >
                    <div
                      className="help-faq-question d-flex justify-content-between align-items-center px-4 py-3 cursor-pointer"
                      onClick={() => setOpenIdx(isOpen ? null : idx)}
                      style={{ transition: "background 0.15s" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-main)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <span
                          className="px-2 py-1 rounded-pill"
                          style={{ fontSize: "10px", fontWeight: 600, background: "rgba(27,187,107,0.1)", color: "#1bbb6b", whiteSpace: "nowrap" }}
                        >
                          {faq.category}
                        </span>
                        <span className="fw-semibold" style={{ fontSize: "14px" }}>{faq.q}</span>
                      </div>
                      <span style={{ color: "var(--text-muted)", flexShrink: 0, marginLeft: "12px" }}>
                        {isOpen ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                      </span>
                    </div>
                    {isOpen && (
                      <div
                        className="px-4 pb-4"
                        style={{ color: "var(--text-muted)", fontSize: "14px", lineHeight: "1.6" }}
                      >
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="help-bottom-cta text-center mt-5">
          <p style={{ color: "var(--text-muted)", fontSize: "14px" }}>
            Still need help?{" "}
            <a href="mailto:support@armf.com" style={{ color: "var(--accent-color)", fontWeight: 600 }}>
              Contact our support team
            </a>
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default HelpCenter;
